import { useEffect, useRef, useState } from "react";

/**
 * Reusable device-viewport frame. Renders `children` at a fixed logical resolution
 * (width×height) and scales the whole thing proportionally to fit its container width.
 *
 * Reusable across MinistryHub — the future Grow "Display Mode" preview should render its
 * (separate) passive renderer inside this same component rather than reinventing scaling.
 */
export const VIEWPORT_PRESETS = [
  { v: "tv", label: "TV (16:9)", w: 1920, h: 1080 },
  { v: "ipad_landscape", label: "iPad Landscape", w: 1024, h: 768 },
  { v: "ipad_portrait", label: "iPad Portrait", w: 768, h: 1024 },
  { v: "desktop", label: "Desktop Browser", w: 1440, h: 900 },
  { v: "mobile", label: "Mobile Phone", w: 390, h: 844 },
];

export const getViewportPreset = (v) => VIEWPORT_PRESETS.find((p) => p.v === v) || VIEWPORT_PRESETS[0];

export function Viewport({ width, height, maxHeight = 640, children, frameClassName = "", innerStyle = {}, ...rest }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const cw = el.clientWidth;
      if (!cw) return;
      const byWidth = cw / width;
      const byHeight = maxHeight ? maxHeight / height : byWidth;
      setScale(Math.min(byWidth, byHeight));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [width, height, maxHeight]);

  return (
    <div ref={containerRef} className="w-full flex justify-center" {...rest}>
      <div
        className={`relative overflow-hidden bg-white shadow-lg ${frameClassName}`}
        style={{ width: width * scale, height: height * scale }}
      >
        {scale > 0 && (
          <div
            style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left", ...innerStyle }}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
